import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../lib/api-client';

interface RepoImage {
  image_id: string;
  url: string;
  language_code: string;
  gender: string;
  age: string;
  role: string;
  location: string;
  emotion: string;
  clothing: string;
  dialog_id: string | null;
  created_at: string;
}

function EditableField({ image, field, color, placeholder, onSave }: {
  image: RepoImage; field: keyof RepoImage; color: string; placeholder: string;
  onSave: (img: RepoImage, field: keyof RepoImage, value: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String(image[field] || ''));

  if (editing) {
    return (
      <input
        autoFocus
        className={`${color} bg-transparent border-b border-indigo-500 outline-none w-full text-xs`}
        value={value}
        onChange={e => setValue(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') { onSave(image, field, value); setEditing(false); }
          if (e.key === 'Escape') { setValue(String(image[field] || '')); setEditing(false); }
        }}
        onBlur={() => { setValue(String(image[field] || '')); setEditing(false); }}
      />
    );
  }

  return (
    <div className={`${color} cursor-pointer hover:underline`} onClick={e => { e.stopPropagation(); setEditing(true); }}>
      {String(image[field] || '') || <span className="text-zinc-600 italic">{placeholder}</span>}
    </div>
  );
}

export default function Images() {
  const [images, setImages] = useState<RepoImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [langFilter, setLangFilter] = useState('');
  const [genderFilter, setGenderFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [languages, setLanguages] = useState<string[]>([]);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  useEffect(() => {
    apiClient<{ languages: string[] }>('/api/admin/dialogs/languages')
      .then(data => { if (data.languages) setLanguages(data.languages.sort()); })
      .catch(() => {});
  }, []);

  const search = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (langFilter) params.set('language', langFilter);
    if (genderFilter) params.set('gender', genderFilter);
    const searchTerm = [roleFilter, locationFilter].filter(Boolean).join(' ');
    if (searchTerm) params.set('search', searchTerm);

    try {
      const data = await apiClient<{ images: RepoImage[] }>(`/api/admin/images/repository?${params}`);
      setImages(data.images || []);
    } catch {
      setImages([]);
    } finally {
      setLoading(false);
    }
  }, [langFilter, genderFilter, roleFilter, locationFilter]);

  const updateImageField = useCallback(async (img: RepoImage, field: keyof RepoImage, value: string) => {
    try {
      await apiClient('/api/admin/images/repository', {
        method: 'POST',
        body: JSON.stringify({ ...img, [field]: value }),
      });
      setImages(prev => prev.map(i => i.image_id === img.image_id ? { ...i, [field]: value } : i));
    } catch {}
  }, []);

  useEffect(() => { search(); }, [search]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-zinc-100">Image Repository</h1>
        <span className="text-sm text-zinc-500">{images.length} images</span>
      </div>

      <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-3">
        <select value={langFilter} onChange={e => setLangFilter(e.target.value)} className="bg-zinc-800 text-zinc-200 rounded px-3 py-2 border border-zinc-700 text-sm">
          <option value="">All languages</option>
          {languages.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
        <select value={genderFilter} onChange={e => setGenderFilter(e.target.value)} className="bg-zinc-800 text-zinc-200 rounded px-3 py-2 border border-zinc-700 text-sm">
          <option value="">All genders</option>
          <option value="male">Male</option>
          <option value="female">Female</option>
        </select>
        <input type="text" placeholder="Role..." value={roleFilter} onChange={e => setRoleFilter(e.target.value)} className="bg-zinc-800 text-zinc-200 rounded px-3 py-2 border border-zinc-700 text-sm col-span-2 sm:col-span-1 sm:w-40" />
        <input type="text" placeholder="Location..." value={locationFilter} onChange={e => setLocationFilter(e.target.value)} className="bg-zinc-800 text-zinc-200 rounded px-3 py-2 border border-zinc-700 text-sm col-span-2 sm:col-span-1 sm:w-40" />
      </div>

      {loading ? (
        <div className="text-zinc-400">Loading images...</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {images.map(img => (
            <div
              key={img.image_id}
              className={`bg-zinc-800 rounded-lg overflow-hidden border-2 transition-colors hover:border-indigo-500 ${img.dialog_id ? 'border-emerald-700' : 'border-transparent'}`}
            >
              <img src={img.url} alt={img.role || ''} className="w-full aspect-[2/3] object-cover cursor-pointer" onClick={() => setLightboxUrl(img.url)} />
              <div className="p-2 text-xs space-y-0.5">
                <EditableField image={img} field="role" color="text-indigo-400 font-semibold" placeholder="role" onSave={updateImageField} />
                <EditableField image={img} field="age" color="text-zinc-400" placeholder="age" onSave={updateImageField} />
                <EditableField image={img} field="clothing" color="text-zinc-400" placeholder="clothing" onSave={updateImageField} />
                <EditableField image={img} field="location" color="text-zinc-500" placeholder="location" onSave={updateImageField} />
                <div className="text-zinc-600">{img.language_code} {img.dialog_id ? <span className="bg-emerald-800 text-emerald-200 px-1 rounded text-[10px]">IN USE</span> : ''}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {lightboxUrl && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-8 cursor-pointer" onClick={() => setLightboxUrl(null)} onKeyDown={e => { if (e.key === 'Escape') setLightboxUrl(null); }} tabIndex={0} ref={el => el?.focus()}>
          <img src={lightboxUrl} alt="Full-size" className="max-w-full max-h-full rounded-lg shadow-2xl cursor-default" onClick={e => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}
